import { useRect } from '@studio-freight/hamo'
import cn from 'clsx'

import { useScroll } from 'hooks/use-scroll'
import { Layout } from 'layouts/default'
import { button, useControls } from 'leva'
import { clamp, mapRange } from 'lib/maths'
import { useStore } from 'lib/store'
import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'
import { useIntersection, useWindowSize } from 'react-use'
import s from '../home/home.module.scss'

const AppearTitle = dynamic(
  () => import('components/appear-title').then((mod) => mod.AppearTitle),
  { ssr: false }
)

const WebGL = dynamic(
  () => import('components/webgl').then(({ WebGL }) => WebGL),
  { ssr: false }
)


export default function Home() {
  const [hasScrolled, setHasScrolled] = useState()
  const zoomRef = useRef(null)
  const [zoomWrapperRect] = useRect()
  const { height: windowHeight } = useWindowSize()

  const [theme, setTheme] = useState('light')
  const lenis = useStore(({ lenis }) => lenis)

  useControls(
    'aryan',
    () => ({
      stop: button(() => {
        lenis.stop()
      }),
      start: button(() => {
        lenis.start()
      }),
    }),
    [lenis]
  )

  useControls(
    'scrollTo',
    () => ({
      immediate: button(() => {
        lenis.scrollTo(30000, { immediate: true })
      }),
      smoothDuration: button(() => {
        lenis.scrollTo(30000, { lock: true, duration: 10 })
      }),
      smooth: button(() => {
        lenis.scrollTo(30000)
      }),
      forceScrollTo: button(() => {
        lenis.scrollTo(30000, { force: true })
      }),
    }),
    [lenis]
  )

  useEffect(() => {
    if (!lenis) return

    function onClassNameChange(lenis) {
      console.log(lenis.className)
    }

    lenis.on('className change', onClassNameChange)

    return () => {
      lenis.off('className change', onClassNameChange)
    }
  }, [lenis])

  useScroll(({ scroll }) => {
    setHasScrolled(scroll > 10)
    if (!zoomWrapperRect.top) return

    const start = zoomWrapperRect.top + windowHeight * 0.5
    const end = zoomWrapperRect.top + zoomWrapperRect.height - windowHeight

    const progress = clamp(0, mapRange(start, end, scroll, 0, 1), 1)
    const center = 0.6
    const progress1 = clamp(0, mapRange(0, center, progress, 0, 1), 1)
    const progress2 = clamp(0, mapRange(center - 0.055, 1, progress, 0, 1), 1)
    setTheme(progress2 === 1 ? 'light' : 'dark')

    zoomRef.current.style.setProperty('--progress1', progress1)
    zoomRef.current.style.setProperty('--progress2', progress2)

    if (progress === 1) {
      zoomRef.current.style.setProperty('background-color', 'currentColor')
    } else {
      zoomRef.current.style.removeProperty('background-color')
    }
  })

  //removed whyRef from the first variable
  const [whyRect] = useRect()
  const [cardsRect] = useRect()
  const [whiteRect] = useRect()
  const [featuresRect] = useRect()
  const [inuseRect] = useRect()

  const addThreshold = useStore(({ addThreshold }) => addThreshold)

  useEffect(() => {
    addThreshold({ id: 'top', value: 0 })
  }, [])

  useEffect(() => {
    const top = whyRect.top - windowHeight / 2
    addThreshold({ id: 'why-start', value: top })
    addThreshold({
      id: 'why-end',
      value: top + whyRect.height,
    })
  }, [whyRect])

  useEffect(() => {
    const top = cardsRect.top - windowHeight / 2
    addThreshold({ id: 'cards-start', value: top })
    addThreshold({ id: 'cards-end', value: top + cardsRect.height })
    addThreshold({
      id: 'red-end',
      value: top + cardsRect.height + windowHeight,
    })
  }, [cardsRect])

  useEffect(() => {
    const top = whiteRect.top - windowHeight
    addThreshold({ id: 'light-start', value: top })
  }, [whiteRect])

  useEffect(() => {
    const top = featuresRect.top
    addThreshold({ id: 'features', value: top })
  }, [featuresRect])

  useEffect(() => {
    const top = inuseRect.top
    addThreshold({ id: 'in-use', value: top })
  }, [inuseRect])

  useEffect(() => {
    const top = lenis?.limit
    addThreshold({ id: 'end', value: top })
  }, [lenis?.limit])

  useScroll((e) => {
    console.log(window.scrollY, e.scroll, e.isScrolling, e.velocity, e.isLocked)
    // console.log(e.isScrolling)
  })

  const inUseRef = useRef()

  const [setIsVisible] = useState(false)
  const intersection = useIntersection(inUseRef, {
    threshold: 0.2,
  })
  useEffect(() => {
    if (intersection?.isIntersecting) {
      setIsVisible(true)
    }
  }, [intersection])

  return (
    <Layout
      theme={theme}
      seo={{
        title: 'Xeno Robotics',
        description:
          'Xeno Robotics is a pioneering engineering firm fusing state-of-the-art robotics with blockchain-driven intelligence to deliver autonomous systems that adapt, learn, and collaborate in diverse environments.',
      }}
      className={s.home}
    >
      <div className={s.canvas}>
        <WebGL />
      </div>

      <section
        className={s.why}
        style={{ marginBottom: '20vh' }}
        // data-lenis-scroll-snap-align="start"
        // ref={whyRef}
      >
        <div className="layout-grid">
          <h2 className={cn(s.sticky, 'h2')}>
            <AppearTitle>About XENO</AppearTitle>
          </h2>
          <aside className={s.features}>
            <div className={s.feature}>
              <p className="p">
                XENO is a{' '}
                <span style={{ color: '#0097FE' }}>
                  Dubai-based robotics and intelligent hardware company
                </span>{' '}
                reimagining how people interact with automation. We design and
                deliver purpose-built robots, smart products, and token-based
                infrastructure that gives users true ownership, personalization,
                and income potential.
              </p>
              <br></br>
              <br></br>
              <p className="p">
                Whether you're customizing a robot for your brand, co-owning a
                robot on rent, or staking tokens into our manufacturing roadmap,
                you're not just buying a product — you're building the
                infrastructure of tomorrow.
              </p>
            </div>
            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>XENO Shop</h3>
              {/* <PalacioLogo className={s.blogos}></PalacioLogo> */}
              <p className="p">
                Our curated robotics catalog features ready-made robots with
                specific functionalities — from hospitality and delivery to
                surveillance and entertainment.
                <br></br>
                <br></br>
                No engineering required. Simply: Choose a robot based on its
                function, customize its look (logo, name, color theme), place
                your order and we’ll build, program, and ship it directly to
                you.
                <br></br>
                <br></br>
                We also offer Web3-native smart devices like our upcoming
                crypto-alert speaker, which keeps you informed about wallet
                activity, DAO proposals, and more in real time.
              </p>
            </div>
            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>Robots on Rent (ROR Model)</h3>
              {/* <FormulaLogo className={s.blogos}></FormulaLogo> */}
              <p className="p">
                Want to earn passive income from the robotics economy? XENO lets
                you co-fund robots that get rented to businesses — restaurants,
                hotels, offices, and beyond.
                <br></br>
                <br></br>
                You provide a portion of the robot’s funding, the robot is
                deployed and rented monthly, and you earn rental income over the
                contract term. Everything is contractually tracked and backed by
                XENO.
              </p>
            </div>
            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>XENO DAO</h3>
              {/* <PokiddoLogo className={s.blogos}></PokiddoLogo> */}
              <p className="p">
                Token holders propose, vote on, and approve new product
                prototypes. Every approved idea goes through prototyping, IP
                filing, and manufacturing — and the revenue gets shared.
                <br></br>
                This isn’t just voting. It’s governance with real-world stakes
                and product ownership.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  )
}

export async function getStaticProps() {
  return {
    props: {
      id: 'home',
    }, // will be passed to the page component as props
  }
}
